import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type JobReportListVM from 'ab-viewModels/report/jobReportList.viewModel';

import { filterMap } from '@acceligentllc/shared/utils/array';
import * as TextUtils from '@acceligentllc/shared/utils/text';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { bemElement } from 'af-utils/bem.util';

import { Button, SearchInput } from '@acceligentllc/storybook';

import PeriodPicker from 'af-components/Controls/PeriodPicker';
import CustomModal from 'af-components/CustomModal';
import SubmitButton from 'af-components/SubmitButton';

import * as WorkRequestActions from 'af-actions/workRequests';

import JobReportList from './JobReportList';
import styles from './styles.module.scss';

const MODAL_TITLE = 'Export Complete Reports Across Multiple Jobs';

interface OwnProps {
	closeModal: () => void;
	onSubmit: (ids: number[], startDate: Date, endDate: Date) => void;
	showModal: boolean;
	initialStartDate: Date;
	initialEndDate: Date;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const _getFilterJobs = (query: string) => {
	return (jobReport: JobReportListVM) => {
		return TextUtils.matches(jobReport, query);
	};
};

const MultipleJobReportsActionModal: React.FC<Props> = (props) => {
	const {
		showModal,
		closeModal,
		findJobReportsList,
		onSubmit,
		initialStartDate,
		initialEndDate,
	} = props;

	const [startDate, setStartDate] = React.useState<Date>(initialStartDate);
	const [endDate, setEndDate] = React.useState<Date>(initialEndDate);
	const [showApprovalWarning, setShowApprovalWarning] = React.useState(false);
	const [searchText, setSearchText] = React.useState<string>('');
	const [selectJobsMap, setSelectJobsMap] = React.useState<{ [id: number]: boolean; }>({});
	const [jobReports, setJobReports] = React.useState<JobReportListVM[]>([]);
	const [filteredJobReports, setFilteredJobReports] = React.useState<JobReportListVM[]>([]);
	const [isValid, setIsValid] = React.useState(false);

	React.useEffect(() => {
		setStartDate(initialStartDate);
	}, [initialStartDate]);

	React.useEffect(() => {
		setEndDate(initialEndDate);
	}, [initialEndDate]);

	React.useEffect(() => {
		const getReports = async () => {
			if (TimeUtils.getDiff(endDate, startDate, 'days', TimeFormat.DATE_ONLY) >= 0) {
				const _reports = await findJobReportsList(startDate, endDate);
				setJobReports(_reports);
				setFilteredJobReports(_reports);
			}
		};
		if (showModal) {
			getReports();
		}
	}, [startDate, endDate, findJobReportsList, showModal]);

	React.useEffect(() => {
		setShowApprovalWarning(jobReports.some((_item) => selectJobsMap[_item.workRequestId] && !_item.areAllReportsInAccounting));
	}, [selectJobsMap, jobReports]);

	React.useEffect(() => {
		const _isValid = Object.values(selectJobsMap).some((_val) => _val);
		setIsValid(_isValid);
	}, [selectJobsMap]);

	const handlePeriodChange = React.useCallback((_startDate: Date, _endDate: Date) => {
		setStartDate(_startDate);
		setEndDate(_endDate);
	}, []);

	const submit = React.useCallback(() => {
		const ids = filterMap(
			Object.entries(selectJobsMap),
			([, value]) => value,
			([key]) => Number(key)
		);
		onSubmit(ids, startDate, endDate);
		setSearchText('');
		setFilteredJobReports([]);
		setSelectJobsMap({});
	}, [selectJobsMap, onSubmit, startDate, endDate]);

	const onSearchInputChange = React.useCallback((_searchText: string) => {
		setSearchText(_searchText);
		if (_searchText) {
			const filteredJobs = jobReports.filter(_getFilterJobs(_searchText));
			setFilteredJobReports(filteredJobs);
		} else {
			setFilteredJobReports(jobReports);
		}
	}, [jobReports]);

	const onValueChange = React.useCallback((id: number, value: boolean) => {
		const _newSelectJobsMap = { ...selectJobsMap, [id]: value };
		setSelectJobsMap(_newSelectJobsMap);
	}, [selectJobsMap]);

	const onSelectAll = React.useCallback(() => {
		const _newSelectJobsMap = {};
		filteredJobReports.forEach((jr) => _newSelectJobsMap[jr.workRequestId] = true);
		setSelectJobsMap(_newSelectJobsMap);
	}, [filteredJobReports]);

	const onClearAll = React.useCallback(() => {
		const _newSelectJobsMap = { ...selectJobsMap };
		filteredJobReports.forEach((jr) => _newSelectJobsMap[jr.workRequestId] = false);
		setSelectJobsMap(_newSelectJobsMap);
	}, [filteredJobReports, selectJobsMap]);

	const onClose = React.useCallback(() => {
		setSearchText('');
		setStartDate(initialStartDate);
		setEndDate(initialEndDate);
		setSelectJobsMap({});
		setJobReports([]);
		setFilteredJobReports([]);
		closeModal();
	}, [closeModal, initialEndDate, initialStartDate]);

	return (
		<CustomModal
			closeModal={onClose}
			modalStyle="info"
			showModal={showModal}
			size="md"
		>
			<CustomModal.Header closeModal={onClose} title={MODAL_TITLE} />
			<CustomModal.Body padding="none">
				{showApprovalWarning &&
					<div className={styles['modal__approved-warning']}>
						Some of the Jobs in the filtered time period are not in "Accounting approved" state.
					</div>
				}
				<div className={styles.modal__container}>
					<Row>
						Select date range and Jobs you wish to download Field Reports from.
						<br />
						All reports will be downloaded as a single file.
					</Row>
					<Row>
						<span className={bemElement('field-report__work-summary__billable-work__legend', 'single')}>
							<span className={`${styles['modal__legend__single__empty-cell']} ${styles['modal__legend__single__empty-cell--selected']}`} />
							This job has some Field Reports in selected date range that have not been approved by accounting
						</span>
					</Row>
					<Row>
						<Col className={styles.modal__col} sm={12}>
							<SearchInput
								name="searchJob"
								onChange={onSearchInputChange}
								placeholder="Search"
								value={searchText}
							/>
						</Col>
						<Col className={styles.modal__col} sm={12}>
							<PeriodPicker
								end={endDate}
								isPopperFixed
								isPopperInline
								onChange={handlePeriodChange}
								start={startDate}
							/>
						</Col>
					</Row>
					<Row>
						<JobReportList
							filterExists={!!searchText}
							items={filteredJobReports}
							onClearAll={onClearAll}
							onSelectAll={onSelectAll}
							onValueChange={onValueChange}
							selectJobsMap={selectJobsMap}
						/>
					</Row>
				</div>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={onClose}
					style="secondary"
				/>
				<SubmitButton
					disabled={!isValid}
					label="Export to PDF"
					onClick={submit}
					variant="primary"
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapDispatchToProps() {
	return {
		findJobReportsList: WorkRequestActions.findJobReportsList,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(MultipleJobReportsActionModal);
